import { useEffect, useState } from 'react';
import type { ID, Project } from '@proofroom/schema';
import { projects as projectsApi } from '../api/endpoints';
import { ApiError } from '../api/client';

export interface MoveDocumentDialogProps {
  open: boolean;
  documentName: string;
  currentProjectId: ID;
  busy: boolean;
  error: string | null;
  onMove(projectId: ID): void;
  onClose(): void;
}

/** Same shape as the viewer's PreviewDialog: a backdrop, one field, Cancel and the action. */
export function MoveDocumentDialog({ open, documentName, currentProjectId, busy, error, onMove, onClose }: MoveDocumentDialogProps) {
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [projectId, setProjectId] = useState<ID>('');
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setProjects(null);
    setLoadError(null);
    projectsApi.list().then((list) => {
      if (cancelled) return;
      // the project it is in now is not somewhere to move it to
      const others = list.filter((p) => p.id !== currentProjectId);
      setProjects(others);
      setProjectId(others[0]?.id ?? '');
    }).catch((err: unknown) => {
      if (!cancelled) setLoadError(err instanceof ApiError ? err.message : 'Could not list the projects');
    });
    return () => { cancelled = true; };
  }, [open, currentProjectId]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;

  return (
    <div className="dialog-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="dialog" role="dialog" aria-modal="true" aria-label="Move to project">
        <h2>Move to project</h2>
        <p className="small muted">Moves {documentName} with its drafts, changes, comments and share links. People in the project it leaves lose sight of it unless they are in the new one too.</p>
        <div className="field">
          {projects === null && !loadError && <span className="small muted">Loading projects</span>}
          {projects !== null && projects.length === 0 && <span className="small muted">There is no other project to move this document to.</span>}
          {projects !== null && projects.length > 0 && (
            <select className="input" aria-label="Project" value={projectId} onChange={(e) => setProjectId(e.target.value)} disabled={busy}>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          )}
        </div>
        {(error ?? loadError) && <p className="badge danger">{error ?? loadError}</p>}
        <div className="btn-row" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
          <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" onClick={() => onMove(projectId)} disabled={busy || !projectId}>{busy ? 'Moving' : 'Move'}</button>
        </div>
      </div>
    </div>
  );
}
